/**
 * @file useFocusedRow.ts
 * @description Подсветка строки, на которую привёл deep-link `?focus=<id>`
 * (phase19.24-order-card-chain-design.md §4).
 *
 * У платежа, отгрузки и возврата нет своей страницы и номера, поэтому узел
 * «Пути заказа» ведёт во вкладку и показывает нужную строку: прокрутка к ней
 * плюс обводка на пару секунд. Без обводки переход выглядит как «ничего не
 * произошло» — тот же урок, что у складского `useDocumentRowHighlight`.
 *
 * Строку ищем по `data-row-id`: таблица и карточки живут в DOM одновременно,
 * и один `id` был бы в документе дважды.
 */

import { useEffect, useState } from "react";

/** Сколько держится обводка: «около 2 секунд» по §4. */
const HIGHLIGHT_MS = 2200;

export interface FocusedRow {
  /** id подсвеченной строки; null — подсветки нет. */
  highlightedId: string | null;
  /** Атрибут строки: `<div {...rowAttr(payment.id)} />`. */
  rowAttr: (id: string) => { "data-row-id": string };
}

export function useFocusedRow(focusId: string | null | undefined): FocusedRow {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  useEffect(() => {
    if (!focusId) {
      setHighlightedId(null);
      return;
    }
    setHighlightedId(focusId);
    const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_MS);
    // Кадр ожидания: вкладка только что смонтировалась, строки ещё нет в DOM.
    const frame = requestAnimationFrame(() => {
      const nodes = document.querySelectorAll<HTMLElement>(
        `[data-row-id="${cssEscape(focusId)}"]`,
      );
      const target =
        Array.from(nodes).find((node) => node.offsetParent !== null) ?? nodes[0];
      // В jsdom `scrollIntoView` не реализован — тест не должен падать.
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [focusId]);

  return {
    highlightedId,
    rowAttr: (id: string) => ({ "data-row-id": id }),
  };
}

/** id из адреса приходит от пользователя — экранируем перед подстановкой. */
function cssEscape(value: string): string {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(value)
    : value.replace(/["\\]/g, "\\$&");
}
